import { useEffect } from "react";

/**
 * Lightweight per-route SEO hook.
 *
 * Sets document.title, meta description, canonical link, og:title,
 * og:description and (optionally) noindex on the current page. Restores
 * the previous values on unmount so we never leave a stale title after
 * navigating away.
 *
 * We deliberately avoid react-helmet-async: this hook is 25 lines, has
 * zero runtime dependencies, and handles the only SEO concerns Google
 * actually crawls on a JS SPA (title + description + canonical).
 *
 * @param {object} opts
 * @param {string} opts.title            Full <title> content (append " · Nomad Life" yourself if wanted).
 * @param {string} [opts.description]    Meta description text (max ~160 chars).
 * @param {string} [opts.path]           Optional canonical path (defaults to window.location.pathname).
 * @param {boolean} [opts.noindex=false] Add <meta name="robots" content="noindex">.
 */
export function useSeo({ title, description, path, noindex = false }) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const ensure = (selector, create) => {
      let el = document.head.querySelector(selector);
      let createdByUs = false;
      if (!el) {
        el = create();
        document.head.appendChild(el);
        createdByUs = true;
      }
      return { el, createdByUs };
    };

    const prev = {};

    // description
    if (description) {
      const { el, createdByUs } = ensure(
        'meta[name="description"]',
        () => Object.assign(document.createElement("meta"), { name: "description" })
      );
      prev.description = { el, createdByUs, value: el.getAttribute("content") };
      el.setAttribute("content", description);

      // og:description mirror — most social crawlers still read the OG tag.
      const og = ensure(
        'meta[property="og:description"]',
        () => { const m = document.createElement("meta"); m.setAttribute("property", "og:description"); return m; }
      );
      prev.ogDescription = { el: og.el, createdByUs: og.createdByUs, value: og.el.getAttribute("content") };
      og.el.setAttribute("content", description);
    }

    // og:title mirror
    if (title) {
      const og = ensure(
        'meta[property="og:title"]',
        () => { const m = document.createElement("meta"); m.setAttribute("property", "og:title"); return m; }
      );
      prev.ogTitle = { el: og.el, createdByUs: og.createdByUs, value: og.el.getAttribute("content") };
      og.el.setAttribute("content", title);
    }

    // canonical — use production origin so preview canonicals don't confuse crawlers
    const origin = "https://nomadlife.top";
    const canonicalHref = origin + (path || window.location.pathname || "/");
    const { el: canonicalEl, createdByUs: canonicalCreated } = ensure(
      'link[rel="canonical"]',
      () => { const l = document.createElement("link"); l.setAttribute("rel", "canonical"); return l; }
    );
    prev.canonical = { el: canonicalEl, createdByUs: canonicalCreated, value: canonicalEl.getAttribute("href") };
    canonicalEl.setAttribute("href", canonicalHref);

    // robots noindex — for thin auth/utility pages so they don't dilute rankings
    let robotsCreated = false;
    let robotsEl = null;
    if (noindex) {
      const { el, createdByUs } = ensure(
        'meta[name="robots"]',
        () => Object.assign(document.createElement("meta"), { name: "robots" })
      );
      robotsEl = el;
      robotsCreated = createdByUs;
      prev.robots = { value: el.getAttribute("content") };
      el.setAttribute("content", "noindex, follow");
    }

    return () => {
      document.title = prevTitle;
      Object.entries(prev).forEach(([key, snapshot]) => {
        if (!snapshot || key === "robots") return;
        if (snapshot.createdByUs) {
          snapshot.el.remove();
        } else if (snapshot.value !== null && snapshot.value !== undefined) {
          const attr = snapshot.el.tagName === "LINK" ? "href" : "content";
          snapshot.el.setAttribute(attr, snapshot.value);
        }
      });
      if (robotsEl) {
        if (robotsCreated) robotsEl.remove();
        else if (prev.robots?.value) robotsEl.setAttribute("content", prev.robots.value);
      }
    };
  }, [title, description, path, noindex]);
}
